import { Link, useLocation, Navigate } from "react-router-dom";
import { MapPin, Phone, Mail, AlertCircle, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { THERAPIST } from "@/data/catalog";
import { STYLE_BY_SLUG } from "@/data/massageStyles";
import ReserveButton from "@/components/ReserveButton";
import useSEO from "@/hooks/useSEO";

/**
 * Dynamic massage style SEO page — one route per technique (thaï, suédois,
 * californien, lomi lomi, deep tissue, sur chaise). Unique content per style.
 */
export default function StylePage() {
    const { pathname } = useLocation();
    const slug = pathname.replace(/^\/+/, "").split("/")[0];
    const style = STYLE_BY_SLUG[slug];

    const canonical = style ? `https://massoclic.ca/${style.slug}` : "https://massoclic.ca/";

    const jsonLd = style ? {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: style.name,
        description: style.description,
        url: canonical,
        provider: {
            "@type": "HealthAndBeautyBusiness",
            name: "Jérémy Tessier — Massothérapie",
            url: "https://massoclic.ca",
            telephone: THERAPIST.phone,
            address: {
                "@type": "PostalAddress",
                streetAddress: "1436 boulevard Curé-Labelle",
                addressLocality: "Blainville",
                addressRegion: "QC",
                postalCode: "J7C 2P4",
                addressCountry: "CA",
            },
        },
        offers: {
            "@type": "Offer",
            price: style.price,
            priceCurrency: "CAD",
            availability: "https://schema.org/InStock",
        },
        areaServed: [
            { "@type": "AdministrativeArea", name: "Laurentides" },
            { "@type": "City", name: "Blainville" },
            { "@type": "AdministrativeArea", name: "Rive-Nord" },
        ],
    } : undefined;

    useSEO({
        title: style?.title,
        description: style?.description,
        canonical,
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd,
    });

    if (!style) return <Navigate to="/" replace />;

    return (
        <main data-testid={`style-page-${style.slug}`} className="overflow-hidden">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-14">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#3D8C95]/10 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#E07A5F]/10 blur-3xl" />
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Sparkles size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Technique de massage
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        {style.name}{" "}
                        <span className="italic text-[#E07A5F]">à Blainville</span>
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-3xl">
                        {style.heroLead}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3 items-center">
                        <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#E8E2D9]">
                            <Clock size={14} className="text-[#3D8C95]" />
                            <span className="text-sm text-[#2C2A29]">
                                {style.duration} min · <span className="text-[#E07A5F] font-medium">{style.price} $</span>
                            </span>
                        </div>
                        <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#E8E2D9]">
                            <MapPin size={14} className="text-[#C44569]" />
                            <span className="text-sm text-[#2C2A29]">{style.location}</span>
                        </div>
                    </div>

                    <div className="mt-6">
                        <ReserveButton
                            mailSubject={`Réservation — ${style.name}`}
                            mailBody={`Bonjour Jérémy,\n\nJe souhaite réserver un ${style.name} (${style.duration} min · ${style.price} $).\n\n• Date envisagée : \n• Préférences : \n\nMerci !`}
                            quoteSubject={`Devis — ${style.name}`}
                            quoteBody={`Bonjour Jérémy,\n\nJe souhaite un devis pour ${style.name}.\n\n• Contexte : \n• Date : \n• Nombre de participants : \n\nMerci !`}
                        />
                    </div>
                </div>
            </section>

            {/* Unique content */}
            <section className="py-12 md:py-16 bg-white">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    {style.paragraphs.map((p, i) => (
                        <p key={i} className="mt-4 text-[#3A3735] leading-relaxed text-base md:text-lg first:mt-0">
                            {p}
                        </p>
                    ))}
                </div>
            </section>

            {/* Contra-indications (optional) */}
            {style.contraindications && (
                <section className="py-10 bg-[#FAF7F2]">
                    <div className="max-w-3xl mx-auto px-6 md:px-12">
                        <div className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-3">
                                <AlertCircle size={18} className="text-[#C44569]" />
                                <h3 className="font-display text-xl text-[#2C2A29]">Contre-indications</h3>
                            </div>
                            <p className="text-sm text-[#6E6862] mb-3">
                                Ce massage n&apos;est pas recommandé en cas de :
                            </p>
                            <ul className="text-sm text-[#3A3735] space-y-1.5">
                                {style.contraindications.map((c, i) => (
                                    <li key={i}>• {c}</li>
                                ))}
                            </ul>
                            <p className="text-xs text-[#6E6862] mt-4 italic">
                                En cas de doute, un billet médical peut être demandé. Contactez-moi avant la séance.
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="py-16 md:py-24 text-center">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <h2 className="font-display text-3xl md:text-5xl text-[#2C2A29] leading-tight">
                        Prêt à réserver votre {style.shortName} ?
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed">
                        Confirmation par courriel. Reçu d&apos;assurance fourni.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <ReserveButton
                            mailSubject={`Réservation — ${style.name}`}
                            mailBody={`Bonjour Jérémy,\n\nJe souhaite réserver un ${style.name} (${style.duration} min · ${style.price} $).\n\n• Date envisagée : \n• Préférences : \n\nMerci !`}
                            quoteSubject={`Devis — ${style.name}`}
                            quoteBody={`Bonjour Jérémy,\n\nJe souhaite un devis pour ${style.name}.\n\n• Contexte : \n• Date : \n• Nombre de participants : \n\nMerci !`}
                        />
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button
                                variant="outline"
                                className="rounded-full border-[#3D8C95] text-[#3D8C95] hover:bg-[#3D8C95] hover:text-white px-8 py-6"
                            >
                                <Phone size={16} className="mr-2" />
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                    <div className="mt-8">
                        <Link
                            to="/menu"
                            className="inline-flex items-center gap-2 text-sm text-[#3D8C95] hover:text-[#2C6D75] underline underline-offset-4"
                        >
                            Voir le menu complet
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
